<template>
  <div class="news-page">
    <Header />
    
    <!-- 页面标题区域 -->
    <section class="page-header">
      <div class="container">
        <div class="header-content">
          <h1 class="page-title">新闻资讯</h1>
          <p class="page-subtitle">了解行业动态，掌握最新资讯</p>
          <div class="breadcrumb">
            <router-link to="/">首页</router-link>
            <span class="separator">/</span>
            <span class="current">新闻资讯</span>
          </div>
        </div>
      </div>
    </section>

    <!-- 新闻分类导航 -->
    <section class="news-categories">
      <div class="container">
        <div class="category-tabs">
          <button 
            v-for="category in categories" 
            :key="category.id"
            :class="['tab-btn', { active: activeCategory === category.id }]"
            @click="setActiveCategory(category.id)"
          >
            {{ category.name }}
          </button>
        </div>
      </div>
    </section>

    <!-- 新闻列表 -->
    <section class="news-content">
      <div class="container">
        <div class="news-grid">
          <article 
            v-for="article in filteredNews" 
            :key="article.id"
            class="news-card"
            @click="viewArticle(article.id)"
          >
            <div class="news-image">
              <img :src="article.image" :alt="article.title" />
              <div class="category-tag">{{ article.category }}</div>
            </div>
            <div class="news-content-area">
              <h3 class="news-title">{{ article.title }}</h3>
              <p class="news-excerpt">{{ article.excerpt }}</p>
              <div class="news-meta">
                <span class="date">{{ formatDate(article.date) }}</span>
                <span class="views">{{ article.views }} 阅读</span>
              </div>
            </div>
          </article>
        </div>

        <!-- 分页 -->
        <div class="pagination">
          <button 
            v-for="page in totalPages" 
            :key="page"
            :class="['page-btn', { active: currentPage === page }]"
            @click="setCurrentPage(page)"
          >
            {{ page }}
          </button>
        </div>
      </div>
    </section>

    <!-- 热门资讯侧边栏 -->
    <section class="hot-news">
      <div class="container">
        <div class="hot-news-content">
          <h3 class="section-title">热门资讯</h3>
          <div class="hot-news-list">
            <div 
              v-for="(item, index) in hotNews" 
              :key="item.id"
              class="hot-news-item"
              @click="viewArticle(item.id)"
            >
              <span class="hot-number">{{ index + 1 }}</span>
              <div class="hot-content">
                <h4 class="hot-title">{{ item.title }}</h4>
                <span class="hot-date">{{ formatDate(item.date) }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <Footer />
  </div>
</template>

<script>
import Header from '@/components/Header.vue'
import Footer from '@/components/Footer.vue'
import { newsData, getHotNews } from '@/data/newsData'

export default {
  name: 'News',
  components: {
    Header,
    Footer
  },
  data() {
    return {
      activeCategory: 'all',
      currentPage: 1,
      itemsPerPage: 9,
      categories: [
        { id: 'all', name: '全部' },
        { id: 'industry', name: '行业动态' },
        { id: 'technology', name: '技术前沿' },
        { id: 'policy', name: '政策法规' }
      ],
      news: newsData,
      hotNews: getHotNews(5)
    }
  },
  computed: {
    filteredNews() {
      if (this.activeCategory === 'all') {
        return this.news
      }
      return this.news.filter(item => item.categoryId === this.activeCategory)
    },
    totalPages() {
      return Math.ceil(this.filteredNews.length / this.itemsPerPage)
    }
  },
  methods: {
    setActiveCategory(categoryId) {
      this.activeCategory = categoryId
      this.currentPage = 1
    },
    setCurrentPage(page) {
      this.currentPage = page
    },
    viewArticle(id) {
      // 跳转到文章详情页面
      this.$router.push(`/news/${id}`)
    },
    formatDate(dateString) {
      const date = new Date(dateString)
      return date.toLocaleDateString('zh-CN')
    }
  }
}
</script>

<style scoped>
.news-page {
  min-height: 100vh;
  background: linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%);
}

/* 页面标题区域 */
.page-header {
  background: 
    linear-gradient(135deg, rgba(30, 64, 175, 0.4) 0%, rgba(59, 130, 246, 0.4) 50%, rgba(96, 165, 250, 0.4) 100%),
    url('@/assets/img/banner3.webp');
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
  background-attachment: scroll;
  color: white;
  padding: 120px 0 80px;
  position: relative;
  overflow: hidden;
  min-height: 400px;
}

/* 移动端背景适配：优化裁剪与空间 */
@media (max-width: 768px) {
  .page-header {
    background-position: center;
    background-size: cover;
    background-repeat: no-repeat;
    padding: 100px 0 60px;
    min-height: max(340px, calc(100vw * 9 / 16));
  }
}

.page-header::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: url('data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100"><defs><pattern id="grid" width="10" height="10" patternUnits="userSpaceOnUse"><path d="M 10 0 L 0 0 0 10" fill="none" stroke="rgba(255,255,255,0.1)" stroke-width="0.5"/></pattern></defs><rect width="100" height="100" fill="url(%23grid)"/></svg>');
  opacity: 0.3;
}

.header-content {
  position: relative;
  z-index: 2;
  text-align: center;
}

.page-title {
  font-size: 3.5rem;
  font-weight: 700;
  margin-bottom: 1rem;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.page-subtitle {
  font-size: 1.25rem;
  opacity: 0.9;
  margin-bottom: 2rem;
}

.breadcrumb {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 0.5rem;
  font-size: 1rem;
}

.breadcrumb a {
  color: rgba(255, 255, 255, 0.8);
  text-decoration: none;
  transition: color 0.3s ease;
}

.breadcrumb a:hover {
  color: white;
}

.separator {
  opacity: 0.6;
}

.current {
  color: white;
  font-weight: 500;
}

/* 新闻分类导航 */
.news-categories {
  background: white;
  padding: 2rem 0;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
  position: sticky;
  top: 80px;
  z-index: 10;
}

.category-tabs {
  display: flex;
  justify-content: center;
  gap: 1rem;
  flex-wrap: wrap;
}

.tab-btn {
  padding: 0.75rem 2rem;
  border: 2px solid #e5e7eb;
  background: white;
  color: #6b7280;
  border-radius: 50px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;
}

.tab-btn:hover {
  border-color: #3b82f6;
  color: #3b82f6;
  transform: translateY(-2px);
}

.tab-btn.active {
  background: linear-gradient(135deg, #3b82f6, #1e40af);
  color: white;
  border-color: transparent;
  box-shadow: 0 4px 15px rgba(59, 130, 246, 0.3);
}

/* 新闻内容区域 */
.news-content {
  padding: 4rem 0;
}

.news-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(350px, 1fr));
  gap: 2rem;
  margin-bottom: 3rem;
}

.news-card {
  background: white;
  border-radius: 20px;
  overflow: hidden;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);
  transition: all 0.3s ease;
  cursor: pointer;
}

.news-card:hover {
  transform: translateY(-10px);
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.15);
}

.news-image {
  position: relative;
  height: 200px;
  overflow: hidden;
}

.news-image img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.3s ease;
}

.news-card:hover .news-image img {
  transform: scale(1.1);
}

.category-tag {
  position: absolute;
  top: 1rem;
  right: 1rem;
  background: linear-gradient(135deg, #3b82f6, #1e40af);
  color: white;
  padding: 0.5rem 1rem;
  border-radius: 20px;
  font-size: 0.875rem;
  font-weight: 500;
}

.news-content-area {
  padding: 1.5rem;
}

.news-title {
  font-size: 1.25rem;
  font-weight: 600;
  color: #1f2937;
  margin-bottom: 0.75rem;
  line-height: 1.4;
}

.news-excerpt {
  color: #6b7280;
  line-height: 1.6;
  margin-bottom: 1rem;
}

.news-meta {
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 0.875rem;
  color: #9ca3af;
}

/* 分页 */
.pagination {
  display: flex;
  justify-content: center;
  gap: 0.5rem;
}

.page-btn {
  padding: 0.75rem 1rem;
  border: 1px solid #e5e7eb;
  background: white;
  color: #6b7280;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.3s ease;
}

.page-btn:hover {
  border-color: #3b82f6;
  color: #3b82f6;
}

.page-btn.active {
  background: #3b82f6;
  color: white;
  border-color: #3b82f6;
}

/* 热门资讯 */
.hot-news {
  background: white;
  padding: 3rem 0;
  margin-top: 2rem;
}

.hot-news-content {
  max-width: 800px;
  margin: 0 auto;
}

.section-title {
  font-size: 2rem;
  font-weight: 700;
  color: #1f2937;
  text-align: center;
  margin-bottom: 2rem;
  position: relative;
}

.section-title::after {
  content: '';
  position: absolute;
  bottom: -10px;
  left: 50%;
  transform: translateX(-50%);
  width: 60px;
  height: 4px;
  background: linear-gradient(135deg, #3b82f6, #1e40af);
  border-radius: 2px;
}

.hot-news-list {
  display: grid;
  gap: 1rem;
}

.hot-news-item {
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 1rem;
  background: #f8fafc;
  border-radius: 12px;
  cursor: pointer;
  transition: all 0.3s ease;
}

.hot-news-item:hover {
  background: #e2e8f0;
  transform: translateX(5px);
}

.hot-number {
  width: 30px;
  height: 30px;
  background: linear-gradient(135deg, #3b82f6, #1e40af);
  color: white;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 600;
  font-size: 0.875rem;
}

.hot-content {
  flex: 1;
}

.hot-title {
  font-weight: 600;
  color: #1f2937;
  margin-bottom: 0.25rem;
  line-height: 1.4;
}

.hot-date {
  font-size: 0.875rem;
  color: #6b7280;
}

/* 响应式设计 */
@media (max-width: 768px) {
  .page-title {
    font-size: 2.5rem;
  }
  
  .news-grid {
    grid-template-columns: 1fr;
    gap: 1.5rem;
  }
  
  .category-tabs {
    gap: 0.5rem;
  }
  
  .tab-btn {
    padding: 0.5rem 1rem;
    font-size: 0.875rem;
  }
  
  .hot-news-item {
    padding: 0.75rem;
  }
}

@media (max-width: 480px) {
  .page-header {
    padding: 100px 0 60px;
  }
  
  .page-title {
    font-size: 2rem;
  }
  
  .news-content {
    padding: 2rem 0;
  }
  
  .news-card {
    margin: 0 1rem;
  }
}
</style>
